/** Type — draw with letterforms; your text flows along the stroke. */
import type { CreateModule, ModuleContext } from '../../core/module-api';
import { cfgNum, cfgStr } from '../../core/module-api';
import type { PathSegment, Point, Shape } from '../../core/scene';
import { defaultStyle } from '../../core/scene';
import { distance, pointAtLength } from '../../core/geometry';
import type { Glyph } from '../../core/stroke-font';
import { FONT_CAP, FONT_GAP, glyphFor } from '../../core/stroke-font';

const ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7V5h16v2"/><path d="M12 5v14"/><path d="M9 19h6"/></svg>';

export function createType(): CreateModule {
  let pts: Point[] = [];
  let drawing = false;

  return {
    manifest: {
      id: 'type',
      kind: 'create',
      name: 'Type',
      apiVersion: 1,
      description: 'Draw with letterforms — your text flows along the stroke you make.',
      icon: ICON,
      params: [
        { kind: 'text', key: 'text', label: 'Text', default: 'alchemy', placeholder: 'type…' },
        { kind: 'slider', key: 'size', label: 'Size', min: 6, max: 120, step: 1, default: 36 },
      ],
    },

    onPointerDown(s) {
      pts = [{ x: s.x, y: s.y }];
      drawing = true;
    },

    onPointerMove(s) {
      if (!drawing) return;
      const p = { x: s.x, y: s.y };
      const last = pts[pts.length - 1];
      if (!last || distance(last, p) >= 2) pts.push(p);
    },

    onPointerUp() {
      drawing = false;
    },

    getLiveShapes(ctx: ModuleContext): Shape[] {
      if (pts.length < 2) return [];
      const glyphs = [...cfgStr(ctx.config, 'text', 'alchemy')].map(glyphFor).filter((g): g is Glyph => Boolean(g));
      if (glyphs.length === 0) return [];
      const size = cfgNum(ctx.config, 'size', 36);
      const sc = size / FONT_CAP;
      let total = 0;
      for (let i = 1; i < pts.length; i++) total += distance(pts[i - 1], pts[i]);

      const out: Shape[] = [];
      let pen = sc; // small left margin in scaled units
      // Lay glyphs out by their own advance widths (proportional, not monospaced).
      for (let gi = 0; gi < 4000; gi++) {
        const glyph = glyphs[gi % glyphs.length];
        const wScaled = glyph.width * sc;
        const at = pointAtLength(pts, pen + wScaled / 2); // place the glyph centre on the path
        pen += wScaled + FONT_GAP * sc;
        if (pen - FONT_GAP * sc > total) break;
        if (!at) continue;
        const cos = Math.cos(at.angle);
        const sin = Math.sin(at.angle);
        const gw = glyph.width;
        const segs: PathSegment[] = [];
        for (const stroke of glyph.strokes) {
          stroke.forEach(([gx, gy], k) => {
            // centre the glyph on its cell, scale, rotate to the local tangent, place
            const lx = (gx - gw / 2) * sc;
            const ly = (gy - FONT_CAP / 2) * sc;
            const x = at.p.x + lx * cos - ly * sin;
            const y = at.p.y + lx * sin + ly * cos;
            segs.push(k === 0 ? { t: 'M', p: { x, y } } : { t: 'L', p: { x, y } });
          });
        }
        if (segs.length < 2) continue;
        const style = defaultStyle();
        style.color = { ...ctx.ink };
        style.lineWidth = Math.max(0.75, size * 0.05);
        out.push(ctx.newShape({ path: { segments: segs }, style }));
      }
      return out;
    },

    onClear() {
      pts = [];
    },

    onCommit() {
      pts = [];
    },
  };
}
